import {
  OrderJoinOrderDetailsResponseCargoItemsSourceElement,
  OrderJoinOrderDetailsResponseCargoItemsSourceElementCargoItemSource,
  OrderJoinOrderDetailsResponseCargoOrder,
  OrderJoinOrderDetailsResponseCargoOrderRide,
  OrderJoinOrderDetailsResponseNodeExclusion,
  OrderJoinOrderDetailsResponseSolvedOrder,
  OrderJoinOrderDetailsResponseSolvedOrderRide,
  OrderJoinOrderDetailsResponseSolvedOrderRideSeqItem,
  OrderJoinOrderDetailsResponseSolvedOrderRideSeqItemMeta,
  OrderJoinOrderDetailsResponseSolvedOrderRideSeqItemMetaSubject,
  OrderJoinOrderDetailsResponseSolvedOrderRideSeqItemMetaSubjectList,
  OrderJoinOrderDetailsResponseSourceType,
  OrderJoinOrderDetailsResponseTransportingOrder,
  OrderJoinOrderDetailsResponseTransportingOrderCreatePlanEntry,
  OrderJoinOrderDetailsResponseTransportingOrderCreatePlanEntryDriver,
  OrderJoinPreviewResponseDriver,
  OrderJoinPreviewResponsePassenger,
  OrderJoinPreviewResponseRideSeqItem,
  OrderJoinPreviewResponseRideWaypoint,
  OrderJoinPreviewResponseRideWaypointMeta,
  OrderJoinPreviewResponseRideWaypointMetaSubject,
  OrderJoinPreviewResponseRideWaypointMetaSubjectList,
  OrderJoinSuccessResponse,
} from "../../../api/raily/order/join/order-join.response";
import SourceType from "../../../types/source-type";
import OrderJoin, {
  OrderJoinCargoItemsSourceElement,
  OrderJoinCargoItemsSourceElementCargoItemSource,
  OrderJoinCargoOrder,
  OrderJoinCargoOrderRide,
  OrderJoinPreviewDriver,
  OrderJoinPreviewRideSeqItem,
  OrderJoinPreviewRideWaypoint,
  OrderJoinPreviewRideWaypointGroup,
  OrderJoinPreviewRideWaypointMeta,
  OrderJoinPreviewRideWaypointMetaSubject,
  OrderJoinPreviewRideWaypointMetaSubjectList,
  OrderJoinSolvedOrder,
  OrderJoinSolvedOrderRide,
  OrderJoinSolvedOrderRideSeqItem,
  OrderJoinSolvedOrderRideSeqItemMeta,
  OrderJoinSolvedOrderRideSeqItemMetaSubject,
  OrderJoinSolvedOrderRideSeqItemMetaSubjectList,
  OrderJoinTransportingOrder,
  OrderJoinTransportingOrderCreatePlanEntry,
  OrderJoinTransportingOrderCreatePlanEntryDriver,
} from "./order-join";

const createItemSourceListElementSource = (
  cargoItemSource: OrderJoinOrderDetailsResponseCargoItemsSourceElementCargoItemSource,
): OrderJoinCargoItemsSourceElementCargoItemSource => {
  if (
    cargoItemSource.sourceType ==
    OrderJoinOrderDetailsResponseSourceType.EXTERNAL
  ) {
    return {
      sourceType: SourceType.EXTERNAL,
      firstName: cargoItemSource.firstName,
      lastName: cargoItemSource.lastName,
    };
  } else {
    return {
      sourceType: SourceType.INTERNAL,
    };
  }
};

const createItemSourceListElement = (
  element: OrderJoinOrderDetailsResponseCargoItemsSourceElement,
): OrderJoinCargoItemsSourceElement => {
  return {
    cargoSubjectId: element.cargoSubjectId,
    cargoItemSource: createItemSourceListElementSource(element.cargoItemSource),
  };
};

const createItemSourceList = (
  cargoItemsSourceList: OrderJoinOrderDetailsResponseCargoItemsSourceElement[],
): OrderJoinCargoItemsSourceElement[] => {
  return cargoItemsSourceList.map(createItemSourceListElement);
};

const createCargoOrderTransportingOrderPlanEntryDriver = (
  driver: OrderJoinOrderDetailsResponseTransportingOrderCreatePlanEntryDriver,
): OrderJoinTransportingOrderCreatePlanEntryDriver => {
  return {
    id: driver.id,
    firstName: driver.firstName,
    lastName: driver.lastName,
    mobilePrimary: driver.mobilePrimary,
    mobilePrimaryPrefix: driver.mobilePrimaryPrefix,
  };
};

const createCargoOrderTransportingOrderPlanEntry = (
  planEntry: OrderJoinOrderDetailsResponseTransportingOrderCreatePlanEntry,
): OrderJoinTransportingOrderCreatePlanEntry => {
  return {
    driver: createCargoOrderTransportingOrderPlanEntryDriver(planEntry.driver),
  };
};

const createCargoOrderTransportingOrderPlanEntryEntries = (
  planEntryEntries: OrderJoinOrderDetailsResponseTransportingOrderCreatePlanEntry[],
): OrderJoinTransportingOrderCreatePlanEntry[] => {
  return planEntryEntries.map(createCargoOrderTransportingOrderPlanEntry);
};

const createCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubject = (
  subject: OrderJoinOrderDetailsResponseSolvedOrderRideSeqItemMetaSubject,
): OrderJoinSolvedOrderRideSeqItemMetaSubject => {
  return {
    id: subject.id,
  };
};

const createCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubjects = (
  subjects: OrderJoinOrderDetailsResponseSolvedOrderRideSeqItemMetaSubject[],
): OrderJoinSolvedOrderRideSeqItemMetaSubject[] => {
  return subjects.map(
    createCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubject,
  );
};

const createCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubjectList = (
  subjectList: OrderJoinOrderDetailsResponseSolvedOrderRideSeqItemMetaSubjectList,
): OrderJoinSolvedOrderRideSeqItemMetaSubjectList => {
  return {
    subjects:
      createCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubjects(
        subjectList.subjects,
      ),
  };
};

const createCargoOrderTransportingOrderSolvedOrderRideSeqItemMeta = (
  meta: OrderJoinOrderDetailsResponseSolvedOrderRideSeqItemMeta,
): OrderJoinSolvedOrderRideSeqItemMeta => {
  return {
    cargoEnter:
      createCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubjectList(
        meta.cargoEnter,
      ),
    cargoExit:
      createCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubjectList(
        meta.cargoExit,
      ),
    estimatedTime: meta.estimatedTime,
  };
};

const createCargoOrderTransportingOrderSolvedOrderRideSeqItem = (
  seqItem: OrderJoinOrderDetailsResponseSolvedOrderRideSeqItem,
): OrderJoinSolvedOrderRideSeqItem => {
  return {
    displayName: seqItem.displayName,
    haltingTime: seqItem.haltingTime,
    lat: seqItem.lat,
    lon: seqItem.lon,
    meta: createCargoOrderTransportingOrderSolvedOrderRideSeqItemMeta(
      seqItem.meta,
    ),
    time: seqItem.time,
  };
};

const createCargoOrderTransportingOrderSolvedOrderRideSeqItems = (
  seqItems: OrderJoinOrderDetailsResponseSolvedOrderRideSeqItem[],
): OrderJoinSolvedOrderRideSeqItem[] => {
  return seqItems.map(createCargoOrderTransportingOrderSolvedOrderRideSeqItem);
};

const createCargoOrderTransportingOrderSolvedOrderRide = (
  ride: OrderJoinOrderDetailsResponseSolvedOrderRide,
): OrderJoinSolvedOrderRide => {
  return {
    seq: createCargoOrderTransportingOrderSolvedOrderRideSeqItems(ride.seq),
  };
};

const createCargoOrderTransportingOrderSolvedOrder = (
  solvedOrder: OrderJoinOrderDetailsResponseSolvedOrder | null,
): OrderJoinSolvedOrder | null => {
  if (!solvedOrder) {
    return null;
  }

  return {
    ride: createCargoOrderTransportingOrderSolvedOrderRide(solvedOrder.ride),
  };
};

const createCargoOrderTransportingOrder = (
  transportingOrders: OrderJoinOrderDetailsResponseTransportingOrder,
): OrderJoinTransportingOrder => {
  return {
    createPlanEntryEntries: createCargoOrderTransportingOrderPlanEntryEntries(
      transportingOrders.createPlanEntryEntries,
    ),
    solvedOrder: createCargoOrderTransportingOrderSolvedOrder(
      transportingOrders.solvedOrder,
    ),
  };
};

const createCargoOrderTransportingOrders = (
  transportingOrders: OrderJoinOrderDetailsResponseTransportingOrder[],
): OrderJoinTransportingOrder[] => {
  return transportingOrders.map(createCargoOrderTransportingOrder);
};

const createCargoOrderRideSeqItemWaypointMetaSubject = (
  subject: OrderJoinPreviewResponseRideWaypointMetaSubject,
): OrderJoinPreviewRideWaypointMetaSubject => {
  return {
    id: subject.id,
  };
};

const createCargoOrderRideSeqItemWaypointMetaSubjects = (
  subjects: OrderJoinPreviewResponseRideWaypointMetaSubject[],
): OrderJoinPreviewRideWaypointMetaSubject[] => {
  return subjects.map(createCargoOrderRideSeqItemWaypointMetaSubject);
};

const createCargoOrderRideSeqItemWaypointMetaSubjectList = (
  subjectList: OrderJoinPreviewResponseRideWaypointMetaSubjectList,
): OrderJoinPreviewRideWaypointMetaSubjectList => {
  return {
    subjects: createCargoOrderRideSeqItemWaypointMetaSubjects(
      subjectList.subjects,
    ),
  };
};

const createCargoOrderRideSeqItemWaypointMeta = (
  meta: OrderJoinPreviewResponseRideWaypointMeta,
): OrderJoinPreviewRideWaypointMeta => {
  return {
    cargoEnter: createCargoOrderRideSeqItemWaypointMetaSubjectList(
      meta.cargoEnter,
    ),
    cargoExit: createCargoOrderRideSeqItemWaypointMetaSubjectList(
      meta.cargoExit,
    ),
    id: meta.id,
  };
};

const createCargoOrderRideSeqItemWaypoint = (
  waypoint: OrderJoinPreviewResponseRideWaypoint,
): OrderJoinPreviewRideWaypoint => {
  return {
    displayName: waypoint.displayName,
    haltingTime: waypoint.haltingTime,
    lat: waypoint.lat,
    lon: waypoint.lon,
    meta: createCargoOrderRideSeqItemWaypointMeta(waypoint.meta),
    time: waypoint.time,
  };
};

const createCargoOrderRideSeqItem = (
  seqItem: OrderJoinPreviewResponseRideSeqItem,
): OrderJoinPreviewRideSeqItem => {
  if ("seq" in seqItem) {
    const group: OrderJoinPreviewRideWaypointGroup = {
      seq: seqItem.seq.map(createCargoOrderRideSeqItemWaypoint),
      sequenceOrder: seqItem.sequenceOrder,
    };
    return group;
  } else {
    return createCargoOrderRideSeqItemWaypoint(seqItem);
  }
};

const createCargoOrderRideSeq = (
  seq: OrderJoinPreviewResponseRideSeqItem[],
): OrderJoinPreviewRideSeqItem[] => {
  return seq.map(createCargoOrderRideSeqItem);
};

const createCargoOrderRide = (
  ride: OrderJoinOrderDetailsResponseCargoOrderRide,
): OrderJoinCargoOrderRide => {
  return {
    seq: createCargoOrderRideSeq(ride.seq),
    sequenceOrder: ride.sequenceOrder,
  };
};

const createCargoOrder = (
  cargoOrder: OrderJoinOrderDetailsResponseCargoOrder,
): OrderJoinCargoOrder => {
  return {
    cargoItemsSourceList: createItemSourceList(cargoOrder.cargoItemsSourceList),
    humanId: cargoOrder.humanId,
    id: cargoOrder.id,
    ride: createCargoOrderRide(cargoOrder.ride),
    transportingOrders: createCargoOrderTransportingOrders(
      cargoOrder.transportingOrders,
    ),
  };
};

const createDriver = (
  driver: OrderJoinPreviewResponseDriver,
): OrderJoinPreviewDriver => {
  return {
    firstName: driver.firstName,
    id: driver.id,
    lastName: driver.lastName,
    mobilePrimary: driver.mobilePrimary,
    mobilePrimaryPrefix: driver.mobilePrimaryPrefix,
  };
};

const createDrivers = (
  drivers: OrderJoinPreviewResponseDriver[],
): OrderJoinPreviewDriver[] => {
  return drivers.map(createDriver);
};

const createNodeExclusion = (
  nodeExclusion: OrderJoinOrderDetailsResponseNodeExclusion,
): OrderJoinOrderDetailsResponseNodeExclusion => {
  return {
    excludeApproaching: nodeExclusion.excludeApproaching,
    excludeInside: nodeExclusion.excludeInside,
    excludeReturning: nodeExclusion.excludeReturning,
  };
};

const createPassenger = (
  passenger: OrderJoinPreviewResponsePassenger,
): OrderJoinPreviewResponsePassenger => {
  return {
    firstName: passenger.firstName,
    id: passenger.id,
    lastName: passenger.lastName,
  };
};

const createPassengers = (
  passengers: OrderJoinPreviewResponsePassenger[],
): OrderJoinPreviewResponsePassenger[] => {
  return passengers.map(createPassenger);
};

const create = (
  response: OrderJoinSuccessResponse | null,
): OrderJoin | null => {
  const data = response?.data;
  if (!data || !data.cargoOrder) {
    return null;
  }

  return {
    cargoOrder: createCargoOrder(data.cargoOrder),
    drivers: createDrivers(data.drivers ?? []),
    nodeExclusion: createNodeExclusion(data.nodeExclusion),
    passengers: createPassengers(data.passengers ?? []),
  };
};

const orderJoinFactory = {
  create,
};

export default orderJoinFactory;
